import React, { useState, useEffect, useRef } from 'react'
import {
  Typography,
  Divider,
  Box,
  Popover,
  withStyles,
} from '@material-ui/core'
import { ChromePicker } from 'react-color'
import styles from './styles'
import { track } from '@amplitude/analytics-browser'

const ColorSelection = props => {
  const { classes, selectedData, onColorsChange, currentColors } = props

  const STORAGE_KEY = 'interactiveActivityColors'
  const DEFAULT_COLORS = {
    backgroundColor: '#F4F6F8',
    primaryColor: '#002E47',
  }

  const isManualChange = useRef(false)
  const isInitialized = useRef(false)

  const getStoredColors = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : DEFAULT_COLORS
    } catch (error) {
      console.error('Error reading colors from localStorage:', error)
      return DEFAULT_COLORS
    }
  }

  const saveColorsToStorage = colors => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(colors))
    } catch (error) {
      console.error('Error saving colors to localStorage:', error)
    }
  }

  const storedColors = getStoredColors()
  const [backgroundColor, setBackgroundColor] = useState(
    storedColors.backgroundColor,
  )
  const [primaryColor, setPrimaryColor] = useState(storedColors.primaryColor)

  const [pickerVisibles, setPickerVisibles] = useState({
    backgroundColor: false,
    primaryColor: false,
  })
  const [pageReady, setPageReady] = useState(true)
  const [anchorEl, setAnchorEl] = useState(null)
  const [activeColorType, setActiveColorType] = useState(null)

  useEffect(() => {
    if (!isInitialized.current && onColorsChange) {
      const initialColors = getStoredColors()
      onColorsChange(initialColors)
      isInitialized.current = true
    }
  }, [onColorsChange])

  useEffect(() => {
    if (isManualChange.current) {
      const colors = { backgroundColor, primaryColor }
      saveColorsToStorage(colors)

      if (onColorsChange) {
        onColorsChange(colors)
      }

      isManualChange.current = false
    }
  }, [backgroundColor, primaryColor, onColorsChange])

  const handleColorChange = (color, colorType) => {
    isManualChange.current = true

    if (colorType === 'backgroundColor') {
      track('interactive_activity_bg_color_changed')
      setBackgroundColor(color.hex)
    } else if (colorType === 'primaryColor') {
      track('interactive_activity_primary_color_changed')
      setPrimaryColor(color.hex)
    }
  }

  const openColorPicker = (event, colorType) => {
    setPickerVisibles({
      ...pickerVisibles,
      [colorType]: !pickerVisibles[colorType],
    })
    setAnchorEl(event.currentTarget)
    setActiveColorType(colorType)
  }

  const closeColorPicker = colorType => {
    setPickerVisibles({
      ...pickerVisibles,
      [colorType]: false,
    })
    setAnchorEl(null)
    setActiveColorType(null)
  }

  if (!selectedData) {
    return (
      <Box className={classes.emptyState}>
        <Typography className={classes.header}>No activity selected</Typography>
      </Box>
    )
  }

  return (
    <div className={classes.box}>
      <div>
        <Typography className={classes.header}>
          Customize Appearance{' '}
        </Typography>
        <Typography className={classes.note}>
          Set the background and text colors for your selected activity.{' '}
        </Typography>
      </div>

      <div className={classes.row}>
        <div>
          <Typography className={classes.header}>Background Color</Typography>
        </div>
        <div className={classes.boxIconButton}>
          <div className={classes.inputItem}>
            <div className={classes.pickerBg}>
              <span
                className={classes.pickerSpan}
                style={{
                  backgroundColor: backgroundColor,
                  zIndex: '999999 !important',
                }}
                onClick={event => openColorPicker(event, 'backgroundColor')}
              ></span>
              <Popover
                className={classes.popover}
                id={
                  pickerVisibles.backgroundColor ? 'backgroundColor' : undefined
                }
                open={pickerVisibles.backgroundColor}
                anchorEl={anchorEl}
                onClose={() => closeColorPicker('backgroundColor')}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <ChromePicker
                  disabled={!pageReady}
                  color={backgroundColor}
                  onChange={color =>
                    handleColorChange(color, 'backgroundColor')
                  }
                />
              </Popover>
            </div>
          </div>
          <Typography className={classes.header}>{backgroundColor}</Typography>
        </div>
      </div>

      <div className={classes.row}>
        <div>
          <Typography className={classes.header}>Primary Color</Typography>
        </div>
        <div className={classes.boxIconButton}>
          <div className={classes.inputItem}>
            <div className={classes.pickerBg}>
              <span
                className={classes.pickerSpan}
                style={{
                  backgroundColor: primaryColor,
                  zIndex: '999999 !important',
                }}
                onClick={event => openColorPicker(event, 'primaryColor')}
              ></span>
              <Popover
                className={classes.popover}
                id={pickerVisibles.primaryColor ? 'primaryColor' : undefined}
                open={pickerVisibles.primaryColor}
                anchorEl={anchorEl}
                onClose={() => closeColorPicker('primaryColor')}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <ChromePicker
                  disabled={!pageReady}
                  color={primaryColor}
                  onChange={color => handleColorChange(color, 'primaryColor')}
                />
              </Popover>
            </div>
          </div>
          <Typography className={classes.header}>{primaryColor}</Typography>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(ColorSelection)
